// Video Player

import { useState, useRef, useEffect, useMemo } from 'react';
import ReactPlayer from 'react-player'

//import playicon from "../img/play.svg";
import playicon from "../img/play_dark.svg";

import seekback from "../img/replay_10.svg";
import seekforward from "../img/forward_10.svg";

import loadingicon from "../img/loading2.svg";

import screensizesmall from "../img/screensize-small.svg";
import screensizefull from "../img/screensize-full.svg";
import { translate } from '../functions/translationHelper';


/**
 * Liest die gerade aktiven Cues aus dem TextTrack des <video>-Elements.
 *
 * Der Track bleibt im DOM und wird vom Browser geparst (mode = 'hidden'),
 * aber NICHT gerendert. Das Rendering machen wir selbst, damit Font,
 * Position und Farbe frei stylebar sind.
 *
 * Kein eigener VTT-Parser, kein timeupdate-Polling: 'cuechange' feuert
 * exakt an den Cue-Grenzen.
 */
function useActiveCues(videoEl, enabled) {
  const [cues, setCues] = useState([]);

  useEffect(() => {
    if (!enabled || !videoEl) {
      setCues([]);
      return;
    }

    let track = null;

    const handleCueChange = () => {
      setCues(Array.from(track?.activeCues ?? []));
    };

    // iOS setzt den Mode eigenmaechtig zurueck, u.a. wenn in den
    // Systemeinstellungen "Untertitel & SDH" aktiv ist. Dann wuerde der
    // Browser zusaetzlich seine eigenen Untertitel zeichnen.
    const forceHidden = () => {
      if (track && track.mode !== 'hidden') track.mode = 'hidden';
    };

    const attach = () => {
      const found = Array.from(videoEl.textTracks).find(
        (t) => t.kind === 'subtitles' || t.kind === 'captions'
      );
      if (!found) return;
      if (found === track) {
        forceHidden();
        return;
      }
      track = found;
      track.mode = 'hidden';
      track.addEventListener('cuechange', handleCueChange);
      handleCueChange();
    };

    attach();
    videoEl.textTracks.addEventListener('addtrack', attach);
    videoEl.textTracks.addEventListener('change', forceHidden);
    videoEl.addEventListener('loadedmetadata', attach);

    return () => {
      track?.removeEventListener('cuechange', handleCueChange);
      videoEl.textTracks.removeEventListener('addtrack', attach);
      videoEl.textTracks.removeEventListener('change', forceHidden);
      videoEl.removeEventListener('loadedmetadata', attach);
      setCues([]);
    };
  }, [videoEl, enabled]);

  return cues;
}

function parseVttTimestamp(value) {
  const parts = value.trim().replace(',', '.').split(':');
  const numericParts = parts.map((part) => Number(part));

  if (numericParts.some((part) => !Number.isFinite(part))) {
    return null;
  }

  if (numericParts.length === 3) {
    return numericParts[0] * 3600 + numericParts[1] * 60 + numericParts[2];
  }

  if (numericParts.length === 2) {
    return numericParts[0] * 60 + numericParts[1];
  }

  return numericParts[0] ?? null;
}

function cleanCueText(text) {
  return text
    .replace(/<\/?c(?:\.[^>]*)?>/g, '')
    .replace(/<\/?v(?:\s+[^>]*)?>/g, '')
    .replace(/<\/?lang(?:\s+[^>]*)?>/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

function parseVttCues(text) {
  if (!text) {
    return [];
  }

  return text
    .replace(/^\uFEFF/, '')
    .split(/\n\s*\n/)
    .flatMap((block, index) => {
      const lines = block
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

      if (
        lines.length === 0 ||
        lines[0].startsWith('WEBVTT') ||
        lines[0].startsWith('NOTE') ||
        lines[0].startsWith('STYLE') ||
        lines[0].startsWith('REGION')
      ) {
        return [];
      }

      const timingLineIndex = lines.findIndex((line) => line.includes('-->'));
      if (timingLineIndex === -1) {
        return [];
      }

      const [startPart, endAndSettings] = lines[timingLineIndex].split('-->');
      const startTime = parseVttTimestamp(startPart);
      const endTime = parseVttTimestamp(endAndSettings.trim().split(/\s+/)[0]);
      const cueText = cleanCueText(lines.slice(timingLineIndex + 1).join('\n'));

      if (startTime === null || endTime === null || !cueText) {
        return [];
      }

      return [{
        id: lines[timingLineIndex - 1] || `cue-${index}`,
        startTime,
        endTime,
        text: cueText,
      }];
    });
}

function ContentVideo(props) {

  const ref = useRef(null)

  const [playing, setplaying] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const [duration, setDuration] = useState(0);


  const [loading, setLoading] = useState(true);

  const [fullscreen, setFullscreen] = useState(true);

  // Das native <video>-Element hinter ReactPlayer (v2). Als State, damit
  // der Cue-Hook neu laeuft, sobald es existiert.
  const [videoEl, setVideoEl] = useState(null);

  // Blob-URL der VTT-Datei. Wir laden die Datei selbst per fetch, damit
  // sie durch dieselbe Service-Worker-Route laeuft wie die uebrigen
  // Trail-Assets. <track src="..."> waere ein separater, CORS-pflichtiger
  // Request, der offline still fehlschlaegt (track.cues bleibt leer,
  // ohne Fehlermeldung).
  const [vttUrl, setVttUrl] = useState(null);
  const [vttText, setVttText] = useState('');
  const [showSubtitles, setShowSubtitles] = useState(true);

  useEffect(() => {
    if (!props.active) {
      setplaying(false);
    }
  }, [props.active])

  useEffect(() => {
    console.log(seconds);
  }, [seconds])

  const hasSubtitles =
    typeof props.content?.subtitles === 'string' && props.content.subtitles.trim().length > 0;

  const subtitleSrc = hasSubtitles
    ? props.baseurl + props.content.subtitles.trim()
    : null;

  // Das Content-Objekt kommt bereits sprachspezifisch vom Backend,
  // die Sprache muss also nicht aus dem Dateinamen zurueckgeraten werden.
  const subtitleLanguage = props.language || localStorage.getItem("language") || "en";

  useEffect(() => {
    if (!subtitleSrc) {
      setVttUrl(null);
      setVttText('');
      return;
    }

    let objectUrl = null;
    let cancelled = false;

    fetch(subtitleSrc)
      .then((r) => {
        if (!r.ok) throw new Error(`VTT ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        setVttText(text);
        objectUrl = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
        setVttUrl(objectUrl);
      })
      .catch((err) => {
        // Untertitel sind optional: Video muss weiterlaufen.
        console.warn('Untertitel konnten nicht geladen werden:', err);
        if (!cancelled) {
          setVttUrl(null);
          setVttText('');
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [subtitleSrc]);

  const subtitleTracks = vttUrl ? [{
    kind: 'subtitles',
    src: vttUrl,
    srcLang: subtitleLanguage,
    label: subtitleLanguage.toUpperCase(),
    default: false // WICHTIG: 'true' wuerde den Track auf 'showing' zwingen
  }] : [];

  const nativeCues = useActiveCues(videoEl, Boolean(vttUrl) && props.active && showSubtitles);
  const parsedCues = useMemo(() => parseVttCues(vttText), [vttText]);
  const subtitlesAvailable = Boolean(vttUrl) || parsedCues.length > 0;
  const fallbackCues = useMemo(() => {
    if (!showSubtitles || !props.active || !duration || nativeCues.length > 0) {
      return [];
    }

    const currentTime = seconds * duration;
    return parsedCues.filter((cue) => currentTime >= cue.startTime && currentTime <= cue.endTime);
  }, [duration, nativeCues.length, parsedCues, props.active, seconds, showSubtitles]);
  const cues = nativeCues.length > 0 ? nativeCues : fallbackCues;

  return (
    <div className={`page-content-video ${!fullscreen ? "video-size-contain" : ""}`} onClick={() => setplaying(!playing)}>
      <ReactPlayer
        ref={ref}
        playing={playing}
        url={props.baseurl + props.content.url}
        config={{ file: { attributes: { crossOrigin: 'anonymous' }, tracks: subtitleTracks } }}
        width='100%'
        height='100%'
        playsinline
        /*loop*/
        onEnded={() => props.onEnd()}
        onProgress={(e) => setSeconds(e.played)}
        /*onReady={() => setready(true)}*/
        onDuration={setDuration}
        onWaiting={() => setLoading(true)}
        onReady={() => {
          setLoading(false);
          const internal = ref.current?.getInternalPlayer();
          if (internal instanceof HTMLVideoElement) setVideoEl(internal);
        }}
        progressInterval={200}
      />

      {cues.length > 0 &&
        <div className='videosubtitles' aria-live='polite' aria-atomic='true'>
          {cues.map((cue, i) =>
            <p className='videosubtitlescue' key={cue.id || `${cue.startTime}-${i}`}>{cleanCueText(cue.text)}</p>
          )}
        </div>
      }

      <div className='videoprogress'><div className='videoprogressinner' style={{ width: seconds * 100 + "%" }}></div></div>

      {/*<div className={`videoplayicon ${playing ?  "videoplayiconhidden" : null}`}><img src={playicon} /></div>*/}

      <div className={`videocontrols ${playing && "videocontrolshidden"}`} >
        <div className='audioplaybuttoncontainer'>
          <div className='audioplaysecondarybutton clickable' onClick={(e) => {
            e.stopPropagation();
            ref.current.seekTo(Math.max(0, seconds - 10 / duration));
          }}><img src={seekback} /></div>
          {loading ?
            <div className={'audioplayicon audioplayiconloading'}><img src={loadingicon} /></div>
            :
            <div onClick={() => setplaying(!playing)} className={'audioplayicon clickable'}><img src={playicon} /></div>
          }
          <div className='audioplaysecondarybutton clickable' onClick={(e) => {
            e.stopPropagation();
            ref.current.seekTo(Math.min(0.999, seconds + 10 / duration));
          }}><img src={seekforward} /></div>
        </div>

        {subtitlesAvailable &&
          <button
            type='button'
            className={`videosubtitletoggle clickable ${showSubtitles ? "videosubtitletoggleactive" : ""}`}
            aria-pressed={showSubtitles}
            onClick={(e) => {
              e.stopPropagation();
              setShowSubtitles((visible) => !visible);
            }}
          >
            {translate(showSubtitles ? "video_subtitles_on" : "video_subtitles_off", subtitleLanguage)}
          </button>
        }
      </div>

{!playing &&
      <div className='audioplaysecondarybutton clickable videofullscreenbutton' onClick={(e) => {
          e.stopPropagation();
          setFullscreen(!fullscreen);
        }}><img src={fullscreen ? screensizesmall : screensizefull} /></div>
      }

      {/*
      <div className={`videocontrols ${playing &&  "videocontrolshidden"}`}>
        <img onClick={() => ref.current?.seekTo(ref.current?.getCurrentTime() - 10)} src={seekback} className='seekcontrolimage clickable' />
        <div className="videoplayicon"><img src={playicon} /></div>
        <img onClick={() => ref.current?.seekTo(ref.current?.getCurrentTime() + 10)} src={seekforward} className='seekcontrolimage clickable' />
      </div>
      */}
    </div>

  );
}

export default ContentVideo;
